import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Moon, Search, Sun } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { CONTROL } from "../../constants/testIds";
import { useKairooData } from "../../contexts/KairooDataContext";
import { useTheme } from "../../contexts/ThemeContext";

// UX-CP-01 §1 "Glass top bar: global gym search, environment indicator (Production/Sandbox —
// ties P08-J18), Kairoo operator avatar menu." No dedicated gym-search endpoint exists — filters
// the (Kairoo-only, cross-tenant) gyms list client-side, same class of "no endpoint yet, don't
// invent a domain feature to fake one" honesty as UX-01's own documented adaptations.
const ENVIRONMENT = process.env.REACT_APP_KAIROO_ENV === "sandbox" ? "Sandbox" : "Production";

export function ControlTopBar() {
  const { me, logout } = useKairooData();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const timerRef = useRef(null);

  const gymsQ = useQuery({
    queryKey: ["control-gyms-search"],
    queryFn: async () => (await api.get("/control/gyms")).data.gyms,
    enabled: open,
  });

  const results = useMemo(() => {
    if (!query.trim() || !gymsQ.data) return [];
    const q = query.trim().toLowerCase();
    return gymsQ.data.filter((g) => g.display_name.toLowerCase().includes(q)).slice(0, 6);
  }, [query, gymsQ.data]);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(query.trim().length >= 2), 200);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const initials = (me?.name || me?.email || "K").slice(0, 2).toUpperCase();

  return (
    <header data-testid={CONTROL.topBar} className="glass-surface sticky top-0 z-40 flex items-center gap-4 px-6 py-3 mx-4 mt-4 rounded-[14px]">
      <div className="relative flex-1 max-w-md">
        <div className="flex items-center gap-2 px-3 py-2 rounded-[10px] transition-all duration-150 focus-within:bg-white/40">
          <Search size={16} strokeWidth={1.7} style={{ color: "var(--scarlet-text-secondary)" }} />
          <input
            data-testid={CONTROL.searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder="Search gyms across the platform…"
            className="scarlet-focusable bg-transparent outline-none text-sm w-full placeholder:text-[var(--scarlet-text-secondary)]"
            style={{ color: "var(--scarlet-text)" }}
          />
        </div>
        <AnimatePresence>
          {open && results.length > 0 && (
            <motion.div
              initial={reducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.98, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.98, y: -4 }}
              transition={reducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="glass-dropdown absolute top-full mt-2 left-0 right-0 rounded-[14px] p-2 z-50"
            >
              {results.map((g) => (
                <button
                  key={g.organization_id}
                  data-testid={CONTROL.searchResultRow(g.organization_id)}
                  onClick={() => navigate(`/kairoo/gyms/${g.organization_id}`)}
                  className="w-full text-left px-3 py-2 rounded-[8px] text-sm hover:bg-black/5 flex justify-between scarlet-focusable"
                >
                  <span style={{ color: "var(--scarlet-text)" }}>{g.display_name}</span>
                  <span className="capitalize text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{g.status}</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <span
        data-testid={CONTROL.envIndicator}
        className="text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full shrink-0"
        style={{
          background: ENVIRONMENT === "Production" ? "var(--status-active-bg)" : "var(--status-expiring-bg)",
          color: ENVIRONMENT === "Production" ? "var(--status-active-text)" : "var(--status-expiring-text)",
        }}
      >
        {ENVIRONMENT}
      </span>

      <button
        onClick={toggleTheme}
        aria-label="Toggle day/night theme"
        className="scarlet-focusable p-2 rounded-[10px] hover:bg-black/5 shrink-0"
      >
        {theme === "dark" ? <Sun size={18} strokeWidth={1.7} /> : <Moon size={18} strokeWidth={1.7} />}
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={CONTROL.avatarMenuTrigger} className="scarlet-focusable shrink-0 rounded-full">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="text-white text-xs font-semibold" style={{ background: "#1F2430" }}>{initials}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="glass-dropdown border-0 rounded-[14px]">
          <DropdownMenuLabel>
            <p className="text-sm font-semibold">{me?.name}</p>
            <p className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>{me?.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid={CONTROL.avatarMenuLogout} onClick={logout}>Log out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
